// THE REPLAY'S CLOCK, as arithmetic.
//
// Split out of replayShared.jsx on 20 Sep 2026 so it can be tested by
// the deploy gate: everything else in that file is JSX, which node's
// test runner cannot import, and a countdown that drifts or runs ahead
// is exactly the kind of fault a source-text check cannot catch.
//
// The rule: a replay's countdown must move at the speed the replay is
// running. Every played event is a fixed point — its own recorded
// timestamp — and between events the clock advances by real elapsed
// time multiplied by the playback speed. It is never allowed past the
// next event the viewer has not reached, so a countdown cannot announce
// something the playback has not shown yet.
export function replayClockValue({ anchorAt, anchorReal, speed, nextAt, now }) {
  if (anchorAt == null || anchorReal == null) return null;
  const rate = Number(speed) > 0 ? Number(speed) : 1;
  // A clock never runs backwards, whatever a caller passes for `now`.
  const elapsed = Math.max(0, now - anchorReal);
  const value = anchorAt + elapsed * rate;
  if (nextAt != null && value > nextAt) return nextAt;
  return value;
}

// WHAT IS LEFT OF A WAIT, in virtual (competition) milliseconds.
//
// The same arithmetic behind three things: changing speed mid-wait,
// pausing, and resuming. A wait is stored as the virtual time still to
// run plus the real moment it was last anchored; `since: null` means
// paused, and a paused wait does not shrink however long somebody
// leaves it.
//
// Virtual rather than real time is the whole point: pause at 600x,
// resume at 1x, and what resumes is the competition time that was
// actually left, not the wall-clock remainder of a 600x wait.
export function remainingWait({ remaining, since, speed, now }) {
  if (remaining == null) return 0;
  if (since == null) return Math.max(0, remaining);
  const rate = Number(speed) > 0 ? Number(speed) : 1;
  return Math.max(0, remaining - Math.max(0, now - since) * rate);
}

// THE TWO STRETCHES THE PLAYER USED TO CUT (1.537.0).
//
// A replay at 1x played the real gap between every pair of events, and
// then skipped the two ends: it began ON the first event rather than at
// the competition's start, and after the last event waited a fixed
// 800ms instead of the real time until the answer. Reported 29 Sep 2026:
// "the replay at 1x doesn't play the competition as it happened... it
// looks like the replay is playing the significant part of it to shorten
// the time."
//
// PURE AND HERE, not inline in the player, so the arithmetic can be run
// by a test rather than read by one. The player already learned that
// lesson with the frame logic the widget kept a broken copy of.
//
// Both return milliseconds of COMPETITION time. The player divides by
// the playback speed, as it does for every other gap.

// Start of the competition to its first recorded event. Zero unless the
// viewer asked for it, and zero when either time is missing - a
// recording that never stored its start cannot have a lead-in inferred,
// and guessing one would be the opposite of what this is for.
export function leadInMs({ asItHappened, startedAt, firstEventAt }) {
  if (!asItHappened) return 0;
  if (!Number.isFinite(startedAt) || !Number.isFinite(firstEventAt)) return 0;
  return Math.max(0, firstEventAt - startedAt);
}

// Last recorded event to the moment it was answered. Falls back to the
// player's own short hold when not asked for or not recorded.
//
// CLAMPED AT ZERO: some recordings carry an answeredAt at or before
// their own last event, and a negative wait would schedule a timer in
// the past - which is not a pause at all but a skip.
export function tailMs({ asItHappened, answeredAt, lastEventAt, fallback }) {
  if (!asItHappened) return fallback;
  if (!Number.isFinite(answeredAt) || !Number.isFinite(lastEventAt)) return fallback;
  return Math.max(0, answeredAt - lastEventAt);
}
